import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { withSnackbar } from '../../../utils/SnackbarHOC';
import { getCollaborateurDocBlob } from '../../../services/FileService';
import Modal from '../../../components/Modal/Modal';
import { AppButton } from '../../../components/UI';

const isImageName = (name) => /\.(png|jpe?g|gif|webp)$/i.test(name || '');

function ShowAddDocCollaborateur({ open, onClose, snackbarShowMessage, files, categorie = 'Document', fileName }) {
    const doc = files?.[0];
    const fileKey = doc?.fileName || fileName;
    const [state, setState] = useState({ status: 'loading', url: '', type: '' });

    const load = useCallback(async () => {
        if (!fileKey) {
            setState({ status: 'error', url: '', type: '' });
            return;
        }
        setState({ status: 'loading', url: '', type: '' });
        try {
            const res = await getCollaborateurDocBlob(fileKey);
            const url = window.URL.createObjectURL(res.data);
            setState({ status: 'ready', url, type: res.data?.type || '' });
        } catch (error) {
            setState({ status: 'error', url: '', type: '' });
        }
    }, [fileKey]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => () => {
        if (state.url) window.URL.revokeObjectURL(state.url);
    }, [state.url]);

    const isImage = state.type.startsWith('image/') || isImageName(fileKey);

    const handleDownload = () => {
        if (!state.url) {
            snackbarShowMessage('Impossible de télécharger : fichier introuvable.', 'error', 4000);
            return;
        }
        const link = document.createElement('a');
        link.href = state.url;
        link.download = fileKey;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const title = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    borderRadius: '10px',
                    bgcolor: '#ebf4fb',
                    color: '#03407b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {isImage ? <ImageOutlinedIcon /> : <PictureAsPdfOutlinedIcon />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                    {categorie}
                </Typography>
                <Typography noWrap title={fileKey} sx={{ fontSize: '0.78rem', color: '#64748b' }}>
                    {fileKey || 'Fichier inconnu'}
                </Typography>
            </Box>
        </Box>
    );

    const actions = (
        <>
            <AppButton variant="secondary" onClick={onClose}>Fermer</AppButton>
            <AppButton
                variant="outline"
                startIcon={<OpenInNewIcon />}
                disabled={state.status !== 'ready'}
                onClick={() => window.open(state.url, '_blank', 'noopener,noreferrer')}
            >
                Nouvel onglet
            </AppButton>
            <AppButton startIcon={<FileDownloadIcon />} disabled={state.status !== 'ready'} onClick={handleDownload}>
                Télécharger
            </AppButton>
        </>
    );

    return (
        <Modal open={open} onClose={onClose} title={title} actions={actions} maxWidth="lg">
            <Box
                sx={{
                    height: '72vh',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#f1f5f9',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {state.status === 'loading' && (
                    <Box sx={{ textAlign: 'center', color: '#64748b' }}>
                        <CircularProgress size={34} thickness={4} sx={{ color: '#03407b' }} />
                        <Typography sx={{ mt: 1.5, fontSize: '0.85rem' }}>Chargement du document…</Typography>
                    </Box>
                )}

                {state.status === 'error' && (
                    <Box sx={{ textAlign: 'center', px: 3, maxWidth: 420 }}>
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                mx: 'auto',
                                borderRadius: '14px',
                                bgcolor: '#fef2f2',
                                color: '#dc2626',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <ErrorOutlineIcon />
                        </Box>
                        <Typography sx={{ mt: 1.5, fontWeight: 800, color: '#0f172a' }}>Document introuvable</Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '0.82rem', color: '#64748b' }}>
                            Le fichier n'a pas pu être chargé. Il a peut-être été supprimé du serveur ; vous pouvez réessayer ou le déposer à nouveau.
                        </Typography>
                        <AppButton size="small" variant="outline" startIcon={<RefreshIcon />} onClick={load} sx={{ mt: 2 }}>
                            Réessayer
                        </AppButton>
                    </Box>
                )}

                {state.status === 'ready' && (isImage ? (
                    <Box
                        component="img"
                        src={state.url}
                        alt={categorie}
                        sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                ) : (
                    <iframe
                        title={`Aperçu ${categorie}`}
                        src={state.url}
                        style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                    />
                ))}
            </Box>
        </Modal>
    );
}

export default withSnackbar(ShowAddDocCollaborateur);
